import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { CertificateV2, CertificatesResponseV2 } from '../api/types';

// Certificates — mirrors the legacy CreateGigOrder StaffingStep, which
// loaded `agency.certificates` (the pickable badge catalog) plus the
// customer's pre-attached defaults. Owns `certificateOptions` (the
// catalog) and signals the orchestrator via `onAutoSeedDefaults` when
// the customer's defaults should populate `jobs[0].certificates` — but
// only when the wizard wasn't prefilled from a template, so the
// template's saved certificate selection wins.
//
// IMPORTANT: the orchestrator's `onAutoSeedDefaults` callback re-checks
// the prefill / already-picked guards inside its setState reducer
// (`autoSeedCertificateDefaultsReducer` in `../stateReducers.ts`) to
// avoid races with template apply or user edits. That reducer is
// unit-tested independently of this hook, so even a timing change in
// the React Query swap below cannot silently regress the
// "don't clobber template prefill" behavior.
const useCertificates = (
  billingAccountId: number | null,
  isPrefilled: boolean,
  onAutoSeedDefaults: (
    defaultIds: number[],
    forBillingAccountId: number | null,
  ) => void,
  /**
   * `unseededDraftsSignature(jobs)` — changes only when a draft exists that
   * should carry the client's badges but does not (empty AND never touched by
   * the admin). Re-running on it recovers a seed that an earlier step
   * overwrote with a stale snapshot. It must NEVER be derived from emptiness
   * alone: that re-seeds a badge the admin just unchecked. See stateReducers.
   */
  unseededSignature = '',
) => {
  const enabled = billingAccountId != null;
  const query = useQuery<CertificatesResponseV2 | null>({
    queryKey: enabled
      ? orderV2QueryKeys.certificates(billingAccountId)
      : ['orders_v2', 'certificates', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .listCertificates(billingAccountId as number)
        .then((response) => response ?? null),
    enabled,
  });

  // Auto-seed signal: fires per successful fetch, and again whenever a draft
  // that should carry the client's badges has none. The orchestrator reducer
  // re-checks `isPrefilled`, the account, and each job's selection/touched
  // state, and returns the same state object when nothing changes — so
  // re-runs are idempotent and cannot loop.
  useEffect(() => {
    const data = query.data;
    if (!data) return;
    if (isPrefilled) return;
    const defaultIds = (data.customer_defaults || []).map((cert) => cert.id);
    if (defaultIds.length === 0) return;
    // Pass the account the data belongs to: a late response from a client
    // the wizard has moved off must not seed the current one.
    onAutoSeedDefaults(defaultIds, billingAccountId);
  }, [query.data, isPrefilled, onAutoSeedDefaults, unseededSignature]);

  const certificateOptions: CertificateV2[] = query.data?.available ?? [];

  return { certificateOptions };
};

export default useCertificates;
